<template>
    <div
        class="ppv2-space-y-2"
        :class="{ isNotrequired: !required, 'floating-label-container': floatingLabel }"
        :style="wrapperStyle"
    >
        <BaseLabel
            :customSizeLabel="customSizeLabel"
            v-if="label && !floatingLabel"
            :label="label"
            :for-id="id"
            :required="required"
            :customClass="customLabelClass"
        />

        <div class="base-time-input-wrapper ppv2-relative" :class="{ 'has-floating-label': floatingLabel }">
            <label
                v-if="label && floatingLabel"
                class="bti-floating-label ppv2-text-opacity-70 hover:ppv2-text-opacity-100"
                :style="floatingLabelStyle"
                :for="id"
            >
                {{ label }}
            </label>
            <DatePicker
                ref="picker"
                :value="pickerInternalValue"
                @input="onPickerInput"
                @open="onOpen()"
                @close="onClose()"
                type="time"
                :format="pickerFormat"
                :value-type="'format'"
                :disabled="disabled"
                :placeholder="placeholder || 'HH:MM'"
                :show-second="false"
                :minute-step="step"
                :disabled-time="isTimeDisabled"
                :use12h="use12h"
                :hour-options="hourOptions"
                :input-class="[
                    'ppv2-h-[40px] ppv2-text-[#A0AEC0] ppv2-bg-black ppv2-bg-opacity-[3%] ppv2-w-full ppv2-border ppv2-border-solid ppv2-border-[#9c9af880] ppv2-rounded-xl ppv2-px-4 ppv2-pr-10 focus:ppv2-outline-none focus:ppv2-border-2 ppv2-cursor-pointer',
                    inputClass,
                ]"
                :input-attr="inputAttrs"
                :popup-class="'base-time-input-popup ppv2-bg-white dark:ppv2-bg-dark'"
                :append-to-body="true"
            >
                <!-- ── Header ── -->
                <template #header="{ emit }">
                    <div class="bti-header">
                        <!-- Left: current time display -->
                        <div class="bti-header-left">
                            <span class="bti-header-time">{{ displayedTime }}</span>
                            <span v-if="use12h" class="bti-header-ampm">{{ amPmLabel }}</span>
                        </div>

                        <!-- Right: Now + format toggle -->
                        <div class="bti-header-actions">
                            <button type="button" class="bti-badge-btn" @click="setNow(emit)">
                                {{ $t('common.now') }}
                            </button>
                            <button
                                type="button"
                                class="bti-badge-btn bti-format-btn"
                                @click="toggleFormat(emit)"
                                :title="use12h ? 'Passer en 24h' : 'Passer en 12h'"
                            >
                                {{ use12h ? '12h' : '24h' }}
                            </button>
                        </div>
                    </div>

                    <!-- AM / PM toggle bar (only in 12h mode) -->
                    <div v-if="use12h" class="bti-ampm-bar">
                        <button
                            type="button"
                            class="bti-ampm-btn"
                            :class="{ active: stagingAmPm === 'AM' }"
                            @click="setAmPm('AM', emit)"
                        >
                            AM
                        </button>
                        <button
                            type="button"
                            class="bti-ampm-btn"
                            :class="{ active: stagingAmPm === 'PM' }"
                            @click="setAmPm('PM', emit)"
                        >
                            PM
                        </button>
                    </div>
                </template>

                <!-- ── Footer ── -->
                <template #footer>
                    <div class="bti-footer">
                        <button type="button" class="bti-btn bti-cancel-btn" @click="onCancel">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="button" class="bti-btn bti-confirm-btn" @click="onConfirm">
                            {{ $t('common.confirm') }}
                        </button>
                    </div>
                </template>
            </DatePicker>

            <!-- Lucide Clock icon -->
            <span v-if="!hideIcon" class="base-input-icon-overlay ppv2-pointer-events-none">
                <Clock :size="17" color="#94A3B8" :strokeWidth="1.5" />
            </span>
        </div>
    </div>
</template>

<script>
import BaseLabel from './BaseLabel.vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { Clock } from 'lucide-vue'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'BaseTimeInput',
    components: { BaseLabel, DatePicker, Clock },
    props: {
        id: { type: String, default: () => `base-time-input-${Math.random().toString(36).substr(2, 9)}` },
        /** Input/output: always HH:mm (24h) */
        value: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        hideIcon: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        placeholder: { type: String, default: '' },
        label: { type: String, default: '' },
        inputClass: { type: String, default: '' },
        customSizeLabel: { type: Number, default: 15 },
        step: { type: Number, default: 1, validator: (v) => [1, 5, 10, 15, 20, 30].includes(v) },
        minTime: { type: String, default: null },
        /** Start in 12h mode */
        defaultUse12h: { type: Boolean, default: false },
        floatingLabel: { type: Boolean, default: false },
        floatingLabelBg: { type: String, default: null },
        customLabelClass: { type: String, default: '' },
    },
    data() {
        return {
            use12h: this.defaultUse12h,
            /** Staging 24h value touched by picker scroll — only committed on Confirm */
            stagingValue24h: this.value || '',
            stagingAmPm: this.getAmPm(this.value),
        }
    },
    computed: {
        wrapperStyle() {
            if (!this.customSizeLabel) return {}
            return { fontSize: normalizeFontSize(this.customSizeLabel, true) }
        },
        inputAttrs() {
            const styles = []
            if (this.customSizeLabel) {
                styles.push(`font-size:${normalizeSize(this.customSizeLabel, true)};`)
                styles.push('font-weight:inherit;')
            }
            return styles.length > 0 ? { style: styles.join('') } : {}
        },

        pickerFormat() {
            return this.use12h ? 'hh:mm' : 'HH:mm'
        },

        /** Value fed into the picker (24h always for value-type consistency) */
        pickerInternalValue() {
            if (!this.stagingValue24h) return null
            if (!this.use12h) return this.stagingValue24h
            // Convert 24h → 12h string for the picker's 12h mode
            const [h, m] = this.stagingValue24h.split(':').map(Number)
            const h12 = h % 12 === 0 ? 12 : h % 12
            return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        },

        displayedTime() {
            if (!this.stagingValue24h) return '--:--'
            if (!this.use12h) return this.stagingValue24h
            const [h, m] = this.stagingValue24h.split(':').map(Number)
            const h12 = h % 12 === 0 ? 12 : h % 12
            return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        },

        amPmLabel() {
            return this.stagingAmPm
        },

        hourOptions() {
            if (!this.use12h) return null
            return Array.from({ length: 12 }, (_, i) => i + 1)
        },

        floatingLabelStyle() {
            const styles = {}
            if (this.floatingLabel && this.floatingLabelBg) {
                styles.backgroundColor = this.floatingLabelBg
            }
            if (this.customSizeLabel) {
                styles.fontSize = normalizeSize(this.customSizeLabel, true)
            }
            return styles
        },
    },
    watch: {
        value(v) {
            this.stagingValue24h = v || ''
            this.stagingAmPm = this.getAmPm(v)
        },
    },
    methods: {
        normalizeFontSize,
        getAmPm(time24) {
            if (!time24) return 'AM'
            const h = parseInt(time24.split(':')[0], 10)
            return h >= 12 ? 'PM' : 'AM'
        },

        onPickerInput(pickerVal) {
            if (!pickerVal) return
            if (!this.use12h) {
                this.stagingValue24h = pickerVal
                this.stagingAmPm = this.getAmPm(pickerVal)
            } else {
                // picker emits hh:mm — convert to 24h using current AM/PM
                this.stagingValue24h = this.to24h(pickerVal, this.stagingAmPm)
            }
        },

        setAmPm(period, emit) {
            this.stagingAmPm = period
            if (this.stagingValue24h) {
                this.stagingValue24h = this.to24h(this.pickerInternalValue, period)
                emit && emit(this.pickerInternalValue)
            }
        },

        /** Convert hh:mm + AM/PM → HH:mm */
        to24h(hhmm, period) {
            const [h, m] = (hhmm || '12:00').split(':').map(Number)
            let h24 = h % 12
            if (period === 'PM') h24 += 12
            return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        },

        toggleFormat(emit) {
            this.use12h = !this.use12h
            // Re-emit the picker internal value to force re-render
            emit && emit(this.pickerInternalValue)
        },

        setNow(emit) {
            const now = new Date()
            const h = now.getHours()
            const m = Math.floor(now.getMinutes() / this.step) * this.step
            this.stagingValue24h = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
            this.stagingAmPm = h >= 12 ? 'PM' : 'AM'
            emit && emit(this.pickerInternalValue)
        },

        onConfirm() {
            this.$emit('input', this.stagingValue24h)
            if (this.$refs.picker) this.$refs.picker.closePopup()
        },

        onCancel() {
            // Revert staging to parent value
            this.stagingValue24h = this.value || ''
            this.stagingAmPm = this.getAmPm(this.value)
            if (this.$refs.picker) this.$refs.picker.closePopup()
        },

        onOpen() {
            // Sync staging with the current parent value when popup opens
            this.stagingValue24h = this.value || ''
            this.stagingAmPm = this.getAmPm(this.value)
        },

        onClose() {
            // Defer reset to next tick so it doesn't conflict with onOpen
            this.$nextTick(() => {
                this.stagingValue24h = this.value || ''
                this.stagingAmPm = this.getAmPm(this.value)
            })
        },

        isTimeDisabled(date) {
            if (!this.minTime) return false
            const [minH, minM] = this.minTime.split(':').map(Number)
            return date.getHours() < minH || (date.getHours() === minH && date.getMinutes() <= minM)
        },
    },
}
</script>

<style>
/* ── Wrapper ── */
.base-time-input-wrapper .mx-datepicker {
    width: 100%;
}
.base-time-input-wrapper .mx-input {
    border-radius: 12px;
    font-size: inherit;
    font-family: inherit;
    cursor: pointer;
    box-shadow: none;
    background: transparent;
}
.base-time-input-wrapper .mx-icon-calendar,
.base-time-input-wrapper .mx-icon-clear {
    display: none !important;
}
.base-time-input-popup .mx-datepicker-btn-confirm {
    display: none !important;
}

/* Custom icon overlay */
.base-input-icon-overlay {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    pointer-events: none;
}

/* ── Popup shell ── */
.base-time-input-popup .mx-datepicker-popup {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    border: 1px solid #e2e8f0;
    min-width: 220px;
}
.base-time-input-popup .mx-time {
    border: none;
    box-shadow: none;
    font-family: 'Poppins', sans-serif;
    width: 100%;
}

/* ── Header ── */
.bti-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 12px 14px 10px;
    gap: 8px;
}
@media (min-width: 320px) {
    .bti-header {
        flex-direction: row;
        align-items: center;
        gap: 4px;
        justify-content: space-between;
    }
}
.bti-header-left {
    display: flex;
    align-items: baseline;
    gap: 6px;
}
.bti-header-time {
    font-size: 24px;
    font-weight: 700;
    color: #000;
    letter-spacing: 2px;
    font-family: 'Poppins', sans-serif;
}
.dark .bti-header-time {
    color: #fff;
}
.bti-header-ampm {
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
    font-family: 'Poppins', sans-serif;
}

.bti-header-actions {
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: flex-end;
}
.bti-badge-btn {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 8px;
    padding: 4px 8px;
    cursor: pointer;
    transition: all 0.15s;
    font-family: 'Poppins', sans-serif;
    white-space: nowrap;
}
.bti-badge-btn:hover {
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
}
.bti-format-btn {
    color: #7d2dbd;
    border-color: rgba(125, 45, 189, 0.4);
    background: rgba(125, 45, 189, 0.12);
}
.bti-format-btn:hover {
    background: rgba(125, 45, 189, 0.25);
    color: #a855f7;
}

/* ── AM/PM bar ── */
.bti-ampm-bar {
    display: flex;
    background: #011326;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.bti-ampm-btn {
    flex: 1;
    padding: 7px 0;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    background: transparent;
    color: #475569;
    transition: all 0.15s;
    font-family: 'Poppins', sans-serif;
    letter-spacing: 1px;
}
.bti-ampm-btn:hover {
    color: #e2e8f0;
}
.bti-ampm-btn.active {
    color: #fff;
    background: rgba(125, 45, 189, 0.35);
}
.bti-ampm-btn:first-child {
    border-right: 1px solid rgba(255, 255, 255, 0.07);
}

/* ── Hide native AM/PM column ── */
/* When use12h is true, vue2-datepicker appends a 3rd column for AM/PM in the list.
   Since we built our own AM/PM top bar, we hide their native 3rd column to save space. */
.base-time-input-popup .mx-time-list .mx-time-column:nth-child(3) {
    display: none !important;
}

/* ── Columns ── */
.base-time-input-popup .mx-time-column .mx-time-item {
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #475569;
    transition: background 0.1s;
}
.base-time-input-popup .mx-time-column .mx-time-item:hover {
    background: #f1f5f9;
    color: #1e293b;
}
.base-time-input-popup .mx-time-column .mx-time-item.active {
    color: #fff;
    font-weight: 700;
    background: #9c9af880;
    border-radius: 8px;
}
.base-time-input-popup .mx-time-column .mx-time-item.disabled {
    color: #cbd5e1;
}

/* ── Footer ── */
.bti-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1px solid #e2e8f0;
    background: #fff;
}
.bti-btn {
    font-size: 12px;
    font-weight: 600;
    border-radius: 10px;
    padding: 6px 16px;
    cursor: pointer;
    transition: background 0.15s;
    font-family: 'Poppins', sans-serif;
    border: none;
}
.bti-cancel-btn {
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;
}
.bti-cancel-btn:hover {
    background: #e2e8f0;
}
.bti-confirm-btn {
    background: #021832;
    color: #fff;
}
.bti-confirm-btn:hover {
    background: #7d2dbd;
}

/* ── DARK MODE ── */
.dark .base-time-input-wrapper .mx-input {
    background: transparent;
    color: #94a3b8;
    border-color: #9c9af880;
}
.dark .base-time-input-popup .mx-datepicker-popup {
    border-color: #334155;
}
.dark .base-time-input-popup .mx-time {
    background: #060b28;
}
.dark .base-time-input-popup .mx-time-column {
    border-color: #334155;
}
.dark .base-time-input-popup .mx-time-column .mx-time-item {
    color: #94a3b8;
}
.dark .base-time-input-popup .mx-time-column .mx-time-item:hover {
    background: #334155;
    color: #f1f5f9;
}
.dark .base-time-input-popup .mx-time-column .mx-time-item.active {
    background: #7d2dbd;
    color: #fff;
}
.dark .bti-footer {
    background: #060b28;
    border-color: transparent;
}
.dark .bti-cancel-btn {
    background: #334155;
    color: #94a3b8;
    border-color: #475569;
}
.dark .bti-cancel-btn:hover {
    background: #475569;
}
.dark .bti-confirm-btn {
    background: #7d2dbd;
    color: #fff;
}
.dark .bti-confirm-btn:hover {
    background: #6d28d9;
}

/* ── Floating Label ── */
.floating-label-container {
    position: relative;
}

.bti-floating-label {
    position: absolute;
    top: 0;
    transform: translateY(-65%);
    left: 12px;
    padding: 0 4px;
    z-index: 10;
    pointer-events: none;
    line-height: 1;
    font-size: inherit;
    opacity: 1 !important;
    white-space: nowrap;
    background-color: #fff;
}

.dark .bti-floating-label {
    background-color: #060b28;
}

.base-time-input-wrapper.has-floating-label {
    margin-top: 8px;
}
</style>
